import {
  ComponentPropsWithoutRef,
  forwardRef,
  useContext,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import cx from "classnames";
import tinycolor from "tinycolor2";

import { LayoutUtilProps, Size } from "../../../types";
import { SrOnly } from "../../SrOnly";
import {
  useAccessibleColor,
  useMergeRefs,
  usePrefersColorScheme,
} from "../../../hooks";

import styles from "./AvatarBase.module.scss";
import { ThemeProviderContext } from "../../../providers/ThemeProvider/internal/ThemeProviderContext";

/**
 * Props for the AvatarBase component
 * @extends ComponentPropsWithoutRef<"span">
 * @extends Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink">
 */
export type AvatarBaseProps = ComponentPropsWithoutRef<"span"> &
  Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink"> & {
    /**
     * Full name
     * @accessibility This is used for alt text and Screenreader
     */
    name: string;
    /**
     * Custom color of the AvatarBase
     */
    color?: string;
    /**
     * Image source to be used for AvatarBase
     */
    image?: string;
    /**
     * Initials to display if no image is provided
     */
    initials?: string;
    /**
     * @default medium
     */
    size?: Extract<Size, "xsmall" | "small" | "medium" | "large">;
    /**
     * Online status of the AvatarBase
     */
    status?: "online" | "offline";
  };

/**
 * Base Avatar component that handles the core avatar functionality.
 *
 * Features:
 * - Renders user avatars with images or initials
 * - Supports custom colors with accessible contrast
 * - Shows online/offline status indicator
 * - Handles image loading errors gracefully
 * - Supports light and dark themes
 * - Fully accessible with screen reader support
 *
 * @example
 * <AvatarBase name="John Doe" size="medium" status="online" />
 */
export const AvatarBase = forwardRef<HTMLSpanElement, AvatarBaseProps>(
  (props, ref) => {
    const {
      className,
      color,
      status,
      name,
      image,
      initials,
      style,
      size,
      ...rest
    } = props;

    const elRef = useRef<HTMLSpanElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const [hasImageErrored, setHasImageErrored] = useState(false);
    const avatarBaseClassNames = cx(styles["avatar-base"], className, {
      [styles[`${size}`]]: size,
    });
    const onlineIndicatorClassNames = cx(styles["indicator"], {
      [styles["offline"]]: !status || status === "offline",
      [styles["online"]]: status && status === "online",
    });

    const { mode: themeMode } = useContext(ThemeProviderContext);
    const { mode: sysMode } = usePrefersColorScheme();
    const mode = themeMode ?? sysMode;

    const { foreground, background, border } = useAccessibleColor(color, mode);

    const styleWithColor = {
      "--int-bg-color": color && background,
      "--int-font-color": color && foreground,
      "--int-border-color": color && border,
      ...style,
    };

    const onlineIndicatorBorderColor = useMemo(() => {
      if (mode === "dark") {
        if (color) {
          return tinycolor(background).brighten(20).toHexString();
        }
      }
      return "var(--background-color)";
    }, [color, mode, background]);

    const onlineIndicatorStyles = {
      "--int-border-color": onlineIndicatorBorderColor,
    } as React.CSSProperties;

    const onlineString = status;
    const srOnly = [name, onlineString].filter(Boolean).join(" ");

    // if the image prop updates, also reset the error state for the new image
    useEffect(() => {
      if (image) {
        setHasImageErrored(false);
      }
    }, [image]);

    // if the image is already complete on our first render (likely due to SSR),
    // check the error state of the already complete image
    useEffect(() => {
      if (image && imgRef.current && imgRef.current.complete) {
        const img = new Image();
        img.onerror = () => setHasImageErrored(true);
        img.src = image;
      }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <span
        className={avatarBaseClassNames}
        style={styleWithColor}
        data-anv="avatarBase"
        {...rest}
        ref={useMergeRefs([elRef, ref])}
      >
        {image && !hasImageErrored ? (
          <img
            className={styles["img"]}
            aria-hidden
            src={image}
            alt={name}
            onError={() => setHasImageErrored(true)}
            ref={imgRef}
          />
        ) : initials ? (
          <span className={styles["initial"]} aria-hidden>
            {initials}
          </span>
        ) : (
          <span aria-hidden>?</span>
        )}

        {status && (
          <span
            aria-hidden
            className={onlineIndicatorClassNames}
            style={onlineIndicatorStyles}
          />
        )}

        <SrOnly>{srOnly}</SrOnly>
      </span>
    );
  },
);

AvatarBase.displayName = "AvatarBase";
